import { baseApi } from 'api/baseApi';
import { useLazyLogoutQuery } from 'api/loginApi';
import { AppWindow, Check, ExternalLink, Globe, LayoutGrid, LogOut, Mail, MoonStar, SquareKanban, Sun } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { SupportedLanguages, useLanguage } from "translations";
import { Button, DropdownMenu, Skeleton } from "ui";
import { isInChromeExtension, isInIframe } from 'utils/proxyUtils';
import useDarkMode from '../../../hooks/use-dark-mode';
import { usePermissions } from '../../../hooks/use-permissions';
import { useAppDispatch } from '../../../store';
import { bootstrapAction } from '../../../store/bootstrapSlice';
import { getSelectedSiteCollaborateInfo, setShowSiteSelectionModal } from '../../../store/siteInfoSlice';
import { getIsBottomBarLoading } from '../../../store/uiSlice';
import { getUser } from '../../../store/userSlice';

type OpenSubMenu = 'language' | 'theme' | null;

export const Language = ({
  isOpen,
  onOpenChange
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage, languages } = useLanguage();

  const handleLanguageChange = (lang: SupportedLanguages) => {
    changeLanguage(lang);
    onOpenChange(false); // Close submenu after selection
  };

  return (
    <DropdownMenu.Sub open={isOpen} onOpenChange={onOpenChange}>
      <DropdownMenu.SubTrigger>
        <Globe size={18} />
        {t('bottom_bar.menu_lang', "Language")}
      </DropdownMenu.SubTrigger>
      <DropdownMenu.SubContent
        className="min-w-[160px] pointer-events-auto"
        alignOffset={-82}
        sideOffset={10}
        container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
      >
        <DropdownMenu.Label className="px-2 py-1.5">
          {t('bottom_bar.lang_head', "Language Settings")}
        </DropdownMenu.Label>
        <DropdownMenu.Separator />
        {Object.keys(languages).map((lang) => (
          <DropdownMenu.Item
            key={lang}
            onSelect={() => handleLanguageChange(lang as SupportedLanguages)}
            className="flex items-center gap-2 px-2 py-1.5 cursor-pointer"
          >
            <span className='truncate'>{languages[lang as SupportedLanguages]}</span>
            {lang === currentLanguage && <Check size={18} className='ml-auto' />}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.SubContent>
    </DropdownMenu.Sub>
  );
};

export const ThemeSelector = ({
  isOpen,
  onOpenChange
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [_resolvedTheme, setTheme, _themePreference] = useDarkMode();

  const handleThemeChange = (theme: 'light' | 'dark') => {
    if (setTheme) {
      setTheme(theme);
    }
    onOpenChange(false); // Close submenu after selection
  };

  return (
    <DropdownMenu.Sub open={isOpen} onOpenChange={onOpenChange}>
      <DropdownMenu.SubTrigger>
        {_resolvedTheme === 'dark' ? <MoonStar size={18} /> : <Sun size={18} />}
        Theme
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          className="min-w-[160px] pointer-events-auto"
          sideOffset={10}
          container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
        >
          <DropdownMenu.Item onClick={() => handleThemeChange('light')}>
            <Sun size={18} /> Light {_resolvedTheme === 'light' && <Check size={18} className='ml-auto' />}
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={() => handleThemeChange('dark')}>
            <MoonStar size={18} /> Dark {_resolvedTheme === 'dark' && <Check size={18} className='ml-auto' />}
          </DropdownMenu.Item>
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
};

export const MiniMenu = () => {
  const { t } = useTranslation();
  const { isPermitted } = usePermissions();
  const dispatch = useAppDispatch();
  const user = useSelector(getUser);
  const siteCollaborateInfo = useSelector(getSelectedSiteCollaborateInfo);
  const [openSubMenu, setOpenSubMenu] = useState<OpenSubMenu>(null);
  const [logout] = useLazyLogoutQuery();
  const [theme] = useDarkMode();
  const isBottomBarLoading = useSelector(getIsBottomBarLoading);

  const navigate = (url: string) => {
    if (isInIframe()) {
      window.parent.postMessage({
        type: 'NAVIGATE',
        url
      }, '*');
    } else {
      const baseUrl = import.meta.env.VITE_APP_ATARIM_URL || process.env.CEB_VITE_APP_ATARIM_URL;
      window.open(`${baseUrl}${url}`, '_blank');
    }
  };

  const handleLogoClick = () => {
    if (siteCollaborateInfo?.result?.setting?.whitelabel.logo_link) {
      const logoLink = siteCollaborateInfo.result?.setting?.whitelabel.logo_link;
      let url;
      try {
        url = new URL(logoLink);
      } catch (e: unknown) {
        url = new URL(`https://${logoLink}`);
        console.error(e);
      }
      window.open(url.href, '_blank', 'noopener,noreferrer');
    }
  };

  const handleNavigationClick = (link: string) => {
    navigate(link);
  };

  const handleSubMenuOpenChange = (subMenu: OpenSubMenu, open: boolean) => {
    if (open) {
      setOpenSubMenu(subMenu);
    } else if (openSubMenu === subMenu) {
      setOpenSubMenu(null);
    }
  };

  const hasPermission = useMemo(() => {
    return siteCollaborateInfo?.result?.role === 'admin' || siteCollaborateInfo?.result?.role === 'contributor';
  }, [siteCollaborateInfo?.result?.role]);

  const collaboratorWhitelabelled = useMemo(() => {
    return siteCollaborateInfo?.result?.role === 'collaborator' && !siteCollaborateInfo?.result?.setting?.whitelabel.favicon.includes('atarim_icon.svg');
  }, [siteCollaborateInfo?.result?.role, siteCollaborateInfo?.result?.setting?.whitelabel.favicon]);

  return (
    <>
      {isBottomBarLoading ? (
        <Skeleton className="h-9 w-9 rounded-md " />
      ) : (
        <>
          <DropdownMenu modal={false}>
            <DropdownMenu.Trigger
              className="rounded-md focus:outline-none focus:ring-1 "
              asChild
            >
              <Button variant="ghost" size="icon" className="h-9 w-9 p-0.5">
                {/* Your existing image logic */}
                <img
                  className="aspect-square"
                  src={
                    siteCollaborateInfo?.result?.setting?.whitelabel.favicon.includes('atarim_icon.svg') && theme === 'dark' ?
                      'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/media/atarim_logo_icon_white.svg' :
                      siteCollaborateInfo?.result?.setting?.whitelabel.favicon
                  }
                />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
              align="center"
              container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
              className="pointer-events-auto"
            >
              {siteCollaborateInfo?.result?.setting?.whitelabel.logo_link && (
                <DropdownMenu.Item onClick={handleLogoClick}>
                  <ExternalLink size={18} />
                  {t('bottom_bar.menu_link', "External Link")}
                </DropdownMenu.Item>
              )}
              {
                !collaboratorWhitelabelled &&
                isPermitted("collaborate-bottom-bar-favicon-all-projects") && (
                  <DropdownMenu.Item onClick={() => handleNavigationClick('/')}>
                    <LayoutGrid size={18} />
                    {t('bottom_bar.menu_home', "Dashboard")}
                  </DropdownMenu.Item>
                )}
              {hasPermission && !collaboratorWhitelabelled && isPermitted("collaborate-bottom-bar-favicon-open-in-inbox") && isPermitted("collaborate-bottom-bar-favicon-open-in-boards") && (
                <>
                  <DropdownMenu.Item onClick={() => handleNavigationClick(`/tasks?website=${siteCollaborateInfo?.result?.id}`)}>
                    <Mail size={18} />
                    {t('bottom_bar.menu_tasks', "Open in Inbox")}
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onClick={() => handleNavigationClick(`/board?website=${siteCollaborateInfo?.result?.id}`)}>
                    <SquareKanban size={18} />
                    {t('bottom_bar.menu_board', "Open in Boards")}
                  </DropdownMenu.Item>
                </>
              )}
              {!isInIframe() && user.loggedIn && !user.isGuest && (
                <DropdownMenu.Item onClick={() => dispatch(setShowSiteSelectionModal(true))}>
                  <AppWindow size={18} />
                  {t('bottom_bar.site_selection', "Select Project")}
                </DropdownMenu.Item>
              )}

              {isPermitted("collaborate-bottom-bar-favicon-language") && (
                <Language
                  isOpen={openSubMenu === 'language'}
                  onOpenChange={(open) => handleSubMenuOpenChange('language', open)}
                />
              )}

              {isPermitted("collaborate-bottom-bar-favicon-theme") && (
                <ThemeSelector
                  isOpen={openSubMenu === 'theme'}
                  onOpenChange={(open) => handleSubMenuOpenChange('theme', open)}
                />
              )}

              {user.loggedIn && !window.ATARIM && (
                <DropdownMenu.Item onClick={async () => {
                  localStorage.removeItem('atarimGuestUser');
                  dispatch({ type: 'LOGOUT' });
                  if (isInChromeExtension()) {
                    await chrome.runtime.sendMessage({ type: 'LOGOUT' });
                  } else {
                    logout().unwrap().then(() => {
                      toast.success(t('bottom_bar.logout_success', 'Logged out successfully'));
                    }).catch((err) => {
                      console.error('Failed to logout', err);
                    }).finally(() => {
                      dispatch(baseApi.util.resetApiState())
                      dispatch(bootstrapAction())
                    });
                  }
                }}>
                  <LogOut size={18} />
                  Log Out
                </DropdownMenu.Item>
              )}
            </DropdownMenu.Content>
          </DropdownMenu>
        </>
      )
      }
    </>
  );
};